import { useMemo } from 'react';
import {
  ProcedureCode,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetPartnershipsQuery,
  useGetPaymentTypesQuery,
  useGetProcedureCodesQuery,
} from '../../types';
import { ProcedureCodeFilterParams } from './procedure-code-filter-bar';

export interface ProcedureCodeRow extends ProcedureCode {
  partnershipName: string;
  insurerName: string;
  clientName: string;
  paymentTypeName: string;
}

export interface ProcedureCodeQueriesResult {
  rows: ProcedureCodeRow[];
  loading: boolean;
  hasError: boolean;
  total: number;
}

export const useProcedureCodeQueries = (
  paginationModel: { page: number; pageSize: number },
  filterParams?: ProcedureCodeFilterParams,
): ProcedureCodeQueriesResult => {
  const {
    data: procedureCodeData,
    previousData: previousProcedureCodeData,
    loading: procedureCodeLoading,
    error: procedureCodeError,
  } = useGetProcedureCodesQuery({
    variables: {
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
      ...(filterParams?.partnershipId && {
        partnershipId: filterParams.partnershipId,
      }),
      ...(filterParams?.insurerId && { insurerId: filterParams.insurerId }),
      ...(filterParams?.clientId && { clientId: filterParams.clientId }),
      ...(filterParams?.code && { code: filterParams.code }),
      ...(filterParams?.paymentType && {
        paymentType: filterParams.paymentType,
      }),
    },
  });

  const {
    data: partnershipsData,
    loading: partnershipsLoading,
    error: partnershipsError,
  } = useGetPartnershipsQuery({ variables: { type: 'billing' } });

  const {
    data: insurersData,
    loading: insurersLoading,
    error: insurersError,
  } = useGetAllInsurersQuery();

  const {
    data: clientsData,
    loading: clientsLoading,
    error: clientsError,
  } = useGetAllClientsQuery();

  const {
    data: paymentTypesData,
    loading: paymentTypesLoading,
    error: paymentTypesError,
  } = useGetPaymentTypesQuery();

  const partnershipMap = useMemo(() => {
    const map = new Map<number, string>();
    partnershipsData?.getPartnerships?.forEach(p => map.set(p.id, p.name));
    return map;
  }, [partnershipsData]);

  const insurerMap = useMemo(() => {
    const map = new Map<number, string>();
    insurersData?.getAllInsurers?.forEach(i => map.set(i.id, i.name));
    return map;
  }, [insurersData]);

  const clientMap = useMemo(() => {
    const map = new Map<number, string>();
    clientsData?.getAllClients?.forEach(c => map.set(c.id, c.name));
    return map;
  }, [clientsData]);

  const paymentTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    paymentTypesData?.getPaymentTypes?.forEach(pt => map.set(pt.id, pt.code));
    return map;
  }, [paymentTypesData]);

  const activeData = procedureCodeData ?? previousProcedureCodeData;

  const rows: ProcedureCodeRow[] = useMemo(() => {
    const items = activeData?.getProcedureCodes?.items ?? [];
    return items.map(item => ({
      ...item,
      // ACCOUNTS-AND-PLAN-MIGRATION: item.partnershipId, item.insurerId, item.clientId are client/insurer/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
      partnershipName: item.partnershipId
        ? (partnershipMap.get(item.partnershipId) ??
          `Unknown (${item.partnershipId})`)
        : '--',
      insurerName: item.insurerId
        ? (insurerMap.get(item.insurerId) ?? `Unknown (${item.insurerId})`)
        : '--',
      clientName: item.clientId
        ? (clientMap.get(item.clientId) ?? `Unknown (${item.clientId})`)
        : '--',
      paymentTypeName: item.paymentTypeId
        ? (paymentTypeMap.get(item.paymentTypeId) ??
          `Unknown (${item.paymentTypeId})`)
        : '--',
    }));
  }, [activeData, partnershipMap, insurerMap, clientMap, paymentTypeMap]);

  const loading =
    procedureCodeLoading ||
    partnershipsLoading ||
    insurersLoading ||
    clientsLoading ||
    paymentTypesLoading;

  const hasError =
    !!procedureCodeError ||
    !!partnershipsError ||
    !!insurersError ||
    !!clientsError ||
    !!paymentTypesError;

  return {
    rows,
    loading,
    hasError,
    total: activeData?.getProcedureCodes?.total ?? 0,
  };
};
